/**
 * Формулы LaTeX → OMML (формулы Word). Поддерживается то, что обычно пишут нейросети
 * и студенты: дроби, индексы и степени, корни, суммы и интегралы, пределы, скобки
 * \left…\right, матрицы и системы (cases), греческие буквы, стрелки и отношения,
 * функции sin, log… Неизвестная команда остаётся текстом, чтобы формула не потерялась.
 */

import { el, textNode, type XElement } from '../ooxml/xml';

type Tok =
  | { t: 'cmd'; v: string }
  | { t: 'text'; v: string }
  | { t: 'char'; v: string }
  | { t: 'num'; v: string }
  | { t: '{' | '}' | '^' | '_' | '&' | 'nl' };

type Stop = (tok: Tok) => boolean;
const NEVER: Stop = () => false;
const THIN = String.fromCharCode(0x2009);

/** Команды, аргумент которых — обычный текст (пробелы в нём значимы). */
const TEXT_CMDS = new Set(['text', 'textrm', 'mathrm', 'mbox', 'operatorname', 'textit', 'textbf', 'mathit', 'mathbf', 'textup', 'mathsf', 'boldsymbol', 'bm']);

const SYMBOLS: Readonly<Record<string, string>> = {
  alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ϵ', varepsilon: 'ε', zeta: 'ζ', eta: 'η', theta: 'θ',
  vartheta: 'ϑ', iota: 'ι', kappa: 'κ', lambda: 'λ', mu: 'μ', nu: 'ν', xi: 'ξ', pi: 'π', varpi: 'ϖ', rho: 'ρ',
  varrho: 'ϱ', sigma: 'σ', varsigma: 'ς', tau: 'τ', upsilon: 'υ', phi: 'ϕ', varphi: 'φ', chi: 'χ', psi: 'ψ', omega: 'ω',
  Gamma: 'Γ', Delta: 'Δ', Theta: 'Θ', Lambda: 'Λ', Xi: 'Ξ', Pi: 'Π', Sigma: 'Σ', Upsilon: 'Υ', Phi: 'Φ', Psi: 'Ψ', Omega: 'Ω',
  cdot: '⋅', times: '×', div: '÷', pm: '±', mp: '∓', le: '≤', leq: '≤', ge: '≥', geq: '≥', ne: '≠', neq: '≠',
  approx: '≈', equiv: '≡', sim: '∼', simeq: '≃', cong: '≅', propto: '∝', infty: '∞', partial: '∂', nabla: '∇',
  to: '→', rightarrow: '→', leftarrow: '←', Rightarrow: '⇒', Leftarrow: '⇐', Leftrightarrow: '⇔', leftrightarrow: '↔',
  iff: '⇔', implies: '⇒', mapsto: '↦', uparrow: '↑', downarrow: '↓', in: '∈', notin: '∉', ni: '∋', subset: '⊂',
  subseteq: '⊆', supset: '⊃', supseteq: '⊇', cup: '∪', cap: '∩', setminus: '∖', emptyset: '∅', varnothing: '∅',
  forall: '∀', exists: '∃', neg: '¬', lnot: '¬', land: '∧', wedge: '∧', lor: '∨', vee: '∨', oplus: '⊕', otimes: '⊗',
  circ: '∘', bullet: '∙', ast: '∗', star: '⋆', prime: '′', degree: '°', angle: '∠', perp: '⊥', parallel: '∥',
  ldots: '…', dots: '…', cdots: '⋯', vdots: '⋮', ddots: '⋱', langle: '⟨', rangle: '⟩', lfloor: '⌊', rfloor: '⌋',
  lceil: '⌈', rceil: '⌉', mid: '∣', vert: '|', Vert: '‖', ll: '≪', gg: '≫', hbar: 'ℏ', ell: 'ℓ', Re: 'ℜ', Im: 'ℑ',
  aleph: 'ℵ', therefore: '∴', because: '∵', triangle: '△', square: '□', lbrace: '{', rbrace: '}',
};

const NARY: Readonly<Record<string, string>> = {
  sum: '∑', prod: '∏', coprod: '∐', int: '∫', iint: '∬', iiint: '∭', oint: '∮', bigcup: '⋃', bigcap: '⋂',
};

/** Функции пишутся прямым шрифтом. */
const FUNCS = new Set([
  'sin', 'cos', 'tan', 'tg', 'cot', 'ctg', 'sec', 'csc', 'arcsin', 'arccos', 'arctan', 'arctg', 'arcctg', 'sinh', 'cosh',
  'tanh', 'sh', 'ch', 'th', 'cth', 'ln', 'log', 'lg', 'exp', 'det', 'deg', 'dim', 'arg', 'ker', 'gcd', 'lcm', 'mod',
  'sgn', 'sign', 'Pr', 'rank', 'const',
]);

/** Операторы, под которыми пишется предел: lim_{x→0}. */
const LIMITS = new Set(['lim', 'max', 'min', 'sup', 'inf', 'limsup', 'liminf', 'argmax', 'argmin']);

const ACCENTS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries({ hat: 0x302, widehat: 0x302, vec: 0x20d7, dot: 0x307, ddot: 0x308, tilde: 0x303, widetilde: 0x303, bar: 0x305, acute: 0x301, grave: 0x300, breve: 0x306, check: 0x30c }).map(
    ([k, code]) => [k, String.fromCharCode(code)],
  ),
);

const DELIMS: Readonly<Record<string, string>> = {
  '{': '{', '}': '}', '|': '‖', langle: '⟨', rangle: '⟩', lvert: '|', rvert: '|', vert: '|', Vert: '‖', lVert: '‖',
  rVert: '‖', lfloor: '⌊', rfloor: '⌋', lceil: '⌈', rceil: '⌉', lbrace: '{', rbrace: '}',
};

const MATRIX_DELIMS: Readonly<Record<string, [string, string]>> = {
  pmatrix: ['(', ')'], bmatrix: ['[', ']'], Bmatrix: ['{', '}'], vmatrix: ['|', '|'], Vmatrix: ['‖', '‖'],
};

const DOUBLE_STRUCK: Readonly<Record<string, string>> = { R: 'ℝ', N: 'ℕ', Z: 'ℤ', Q: 'ℚ', C: 'ℂ' };

const SPACES = new Set([',', ':', ';', ' ', 'quad', 'qquad', 'enspace', 'thinspace']);
const IGNORED = new Set(['displaystyle', 'textstyle', 'scriptstyle', 'limits', 'nolimits', 'big', 'Big', 'bigg', 'Bigg', 'bigl', 'bigr', 'Bigl', 'Bigr', 'biggl', 'biggr', '!', 'nonumber', 'notag']);

/** На этих знаках заканчивается тело суммы или интеграла. */
const RELATIONS = new Set(['pm', 'mp', 'le', 'leq', 'ge', 'geq', 'ne', 'neq', 'approx', 'equiv', 'sim', 'to', 'rightarrow', 'Rightarrow', 'quad', 'qquad']);
const naryStop: Stop = (t) => (t.t === 'char' && '+-=<>,;'.includes(t.v)) || (t.t === 'cmd' && RELATIONS.has(t.v));

function tokenize(s: string): Tok[] {
  const out: Tok[] = [];
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    if (c === '\\') {
      const m = /^\\([a-zA-Z]+|[\s\S])/.exec(s.slice(i));
      if (!m) break;
      i += m[0].length;
      const name = m[1]!;
      if (name === '\\') {
        out.push({ t: 'nl' });
        continue;
      }
      if (TEXT_CMDS.has(name)) {
        while (s[i] === ' ') i++;
        if (s[i] === '{') {
          let depth = 0;
          let j = i;
          for (; j < s.length; j++) {
            if (s[j] === '{') depth++;
            else if (s[j] === '}' && --depth === 0) break;
          }
          out.push({ t: 'text', v: s.slice(i + 1, j) });
          i = j + 1;
          continue;
        }
      }
      out.push({ t: 'cmd', v: name });
      continue;
    }
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (c === '{' || c === '}' || c === '^' || c === '_' || c === '&') {
      out.push({ t: c });
      i++;
      continue;
    }
    const num = /^\d+(?:[.,]\d+)*/.exec(s.slice(i));
    if (num) {
      out.push({ t: 'num', v: num[0] });
      i += num[0].length;
      continue;
    }
    out.push({ t: 'char', v: c });
    i++;
  }
  return out;
}

const M = (name: string, children: XElement[] = [], attrs: Record<string, string> = {}): XElement => el(`m:${name}`, attrs, children);
const val = (name: string, v: string): XElement => M(name, [], { 'm:val': v });

function run(text: string, plain = false): XElement {
  const kids: XElement[] = [];
  if (plain) kids.push(M('rPr', [val('sty', 'p')]));
  kids.push(el('w:rPr', {}, [el('w:rFonts', { 'w:ascii': 'Cambria Math', 'w:hAnsi': 'Cambria Math' })]));
  kids.push(el('m:t', { 'xml:space': 'preserve' }, [textNode(text)]));
  return M('r', kids);
}

/** Текст элемента, если это одиночный фрагмент m:r. */
function runText(els: XElement[]): string | undefined {
  if (els.length !== 1 || els[0]!.name !== 'm:r') return undefined;
  const t = els[0]!.children.find((c): c is XElement => c.type === 'element' && c.name === 'm:t');
  const node = t?.children[0];
  return node?.type === 'text' ? node.value : undefined;
}

class Parser {
  private i = 0;
  readonly unknown = new Set<string>();

  constructor(private readonly toks: Tok[]) {}

  private peek(): Tok | undefined {
    return this.toks[this.i];
  }

  private isChar(v: string): boolean {
    const t = this.peek();
    return t?.t === 'char' && t.v === v;
  }

  list(stop: Stop): XElement[] {
    const out: XElement[] = [];
    while (this.i < this.toks.length && !stop(this.peek()!)) out.push(...this.atom(stop));
    return out;
  }

  /** Аргумент команды: {группа} или один элемент. */
  private arg(): XElement[] {
    if (this.i >= this.toks.length) return [];
    if (this.peek()!.t === '{') {
      this.i++;
      const inner = this.list((t) => t.t === '}');
      this.i++;
      return inner;
    }
    return this.base(NEVER);
  }

  private atom(stop: Stop): XElement[] {
    const tok = this.peek()!;
    if (tok.t === 'cmd' && NARY[tok.v]) return [this.nary(stop)];
    if (tok.t === 'cmd' && LIMITS.has(tok.v)) {
      this.i++;
      const name = run(tok.v, true);
      if (this.peek()?.t !== '_') return [name];
      this.i++;
      return [M('limLow', [M('e', [name]), M('lim', this.arg())])];
    }
    return this.scripts(this.base(stop));
  }

  /** Степень и индекс к только что разобранному основанию; «'» — штрих. */
  private scripts(base: XElement[]): XElement[] {
    let sub: XElement[] | undefined;
    let sup: XElement[] | undefined;
    for (;;) {
      const t = this.peek();
      if (t?.t === '_' && !sub) {
        this.i++;
        sub = this.arg();
      } else if (t?.t === '^' && !sup) {
        this.i++;
        sup = this.arg();
      } else if (this.isChar("'") && !sup) {
        let n = 0;
        while (this.isChar("'")) {
          this.i++;
          n++;
        }
        sup = [run('′'.repeat(n))];
      } else break;
    }
    // 30^\circ — это градусы.
    if (sup && !sub && runText(sup) === '∘') return [...base, run('°')];
    if (!sub && !sup) return base;
    const e = M('e', base);
    if (sub && sup) return [M('sSubSup', [e, M('sub', sub), M('sup', sup)])];
    if (sub) return [M('sSub', [e, M('sub', sub)])];
    return [M('sSup', [e, M('sup', sup!)])];
  }

  private base(stop: Stop): XElement[] {
    const tok = this.toks[this.i++]!;
    switch (tok.t) {
      case 'num':
        return [run(tok.v)];
      case 'text':
        return [run(tok.v, true)];
      case 'char':
        return [run(tok.v === '-' ? '−' : tok.v === '*' ? '∗' : tok.v)];
      case '{': {
        const inner = this.list((t) => t.t === '}');
        this.i++;
        return inner;
      }
      case '^':
      case '_':
        this.i--;
        return this.scripts([]);
      case 'cmd':
        return this.command(tok.v, stop);
      default:
        return [];
    }
  }

  private command(name: string, stop: Stop): XElement[] {
    const sym = SYMBOLS[name];
    if (sym) return [run(sym)];
    if (FUNCS.has(name)) return [run(name, true)];
    const accent = ACCENTS[name];
    if (accent) return [M('acc', [M('accPr', [val('chr', accent)]), M('e', this.arg())])];
    switch (name) {
      case 'frac':
      case 'dfrac':
      case 'tfrac':
      case 'cfrac': {
        const num = this.arg();
        const den = this.arg();
        return [M('f', [M('num', num), M('den', den)])];
      }
      case 'sqrt': {
        let deg: XElement[] | undefined;
        if (this.isChar('[')) {
          this.i++;
          deg = this.list((t) => t.t === 'char' && t.v === ']');
          this.i++;
        }
        const body = this.arg();
        return [M('rad', [M('radPr', deg ? [] : [val('degHide', '1')]), M('deg', deg ?? []), M('e', body)])];
      }
      case 'left':
        return [this.delimited()];
      case 'begin':
        return [this.environment()];
      case 'overline':
        return [M('bar', [M('barPr', [val('pos', 'top')]), M('e', this.arg())])];
      case 'underline':
        return [M('bar', [M('barPr', [val('pos', 'bot')]), M('e', this.arg())])];
      case 'mathbb':
      case 'mathcal':
      case 'mathscr':
      case 'mathfrak': {
        const [a, b, c] = [this.toks[this.i], this.toks[this.i + 1], this.toks[this.i + 2]];
        if (name === 'mathbb' && a?.t === '{' && b?.t === 'char' && c?.t === '}' && DOUBLE_STRUCK[b.v]) {
          this.i += 3;
          return [run(DOUBLE_STRUCK[b.v]!)];
        }
        if (name === 'mathbb' && a?.t === 'char' && DOUBLE_STRUCK[a.v]) {
          this.i++;
          return [run(DOUBLE_STRUCK[a.v]!)];
        }
        return this.arg();
      }
    }
    if (IGNORED.has(name)) return [];
    if (SPACES.has(name)) return [run(name === 'quad' || name === 'qquad' || name === 'enspace' ? ' ' : THIN)];
    if (name.length === 1 && '{}%$#&_|'.includes(name)) return [run(name === '|' ? '‖' : name)];
    void stop;
    this.unknown.add(name);
    return [run(name, true)];
  }

  private delimiter(): string {
    const t = this.toks[this.i++];
    if (!t) return '';
    if (t.t === 'char') return t.v === '.' ? '' : t.v;
    if (t.t === 'cmd') return DELIMS[t.v] ?? '';
    return '';
  }

  private delimited(): XElement {
    const open = this.delimiter();
    const body = this.list((t) => t.t === 'cmd' && t.v === 'right');
    this.i++;
    const close = this.delimiter();
    return M('d', [M('dPr', [val('begChr', open), val('endChr', close)]), M('e', body)]);
  }

  private envName(): string {
    if (this.peek()?.t !== '{') return '';
    this.i++;
    let name = '';
    while (this.i < this.toks.length && this.peek()!.t !== '}') {
      const t = this.toks[this.i++]!;
      if (t.t === 'char' || t.t === 'num' || t.t === 'cmd') name += t.v;
    }
    this.i++;
    return name;
  }

  private environment(): XElement {
    const name = this.envName().replace(/\*$/, '');
    if (name === 'array') this.arg();
    const rows: XElement[][][] = [];
    let row: XElement[][] = [];
    const cellStop: Stop = (t) => (t.t === 'cmd' && t.v === 'end') || t.t === '&' || t.t === 'nl';
    while (this.i < this.toks.length) {
      row.push(this.list(cellStop));
      const t = this.toks[this.i++];
      if (!t || (t.t === 'cmd' && t.v === 'end')) break;
      if (t.t === 'nl') {
        rows.push(row);
        row = [];
      }
    }
    if (row.some((c) => c.length) || !rows.length) rows.push(row);
    this.envName();
    const joined = (r: XElement[][]): XElement[] => r.flatMap((c, k) => (k ? [run(' '), ...c] : c));
    if (name === 'cases') {
      return M('d', [M('dPr', [val('begChr', '{'), val('endChr', '')]), M('e', [M('eqArr', rows.map((r) => M('e', joined(r))))])]);
    }
    if (!name.includes('matrix') && name !== 'array') return M('eqArr', rows.map((r) => M('e', joined(r))));
    const cols = Math.max(1, ...rows.map((r) => r.length));
    const matrix = M('m', rows.map((r) => M('mr', Array.from({ length: cols }, (_, k) => M('e', r[k] ?? [])))));
    const wrap = MATRIX_DELIMS[name];
    return wrap ? M('d', [M('dPr', [val('begChr', wrap[0]), val('endChr', wrap[1])]), M('e', [matrix])]) : matrix;
  }

  private nary(stop: Stop): XElement {
    const name = (this.toks[this.i++] as { v: string }).v;
    let sub: XElement[] | undefined;
    let sup: XElement[] | undefined;
    for (;;) {
      const t = this.peek();
      if (t?.t === '_' && !sub) {
        this.i++;
        sub = this.arg();
      } else if (t?.t === '^' && !sup) {
        this.i++;
        sup = this.arg();
      } else if (t?.t === 'cmd' && (t.v === 'limits' || t.v === 'nolimits')) this.i++;
      else break;
    }
    // Тело — до знака сложения или отношения вне скобок: ∑ (xᵢ − x̄)² = …
    const body: XElement[] = [];
    let depth = 0;
    while (this.i < this.toks.length) {
      const t = this.peek()!;
      if (stop(t) || (depth === 0 && naryStop(t))) break;
      if (t.t === 'char' && (t.v === '(' || t.v === '[')) depth++;
      if (t.t === 'char' && (t.v === ')' || t.v === ']')) depth = Math.max(0, depth - 1);
      body.push(...this.atom(stop));
    }
    const pr = [val('chr', NARY[name]!), val('limLoc', /int$/.test(name) ? 'subSup' : 'undOvr')];
    if (!sub) pr.push(val('subHide', '1'));
    if (!sup) pr.push(val('supHide', '1'));
    return M('nary', [M('naryPr', pr), M('sub', sub ?? []), M('sup', sup ?? []), M('e', body)]);
  }
}

export interface OmmlResult {
  /** Содержимое m:oMath. */
  nodes: XElement[];
  /** Команды, которые не удалось перевести (остались текстом). */
  unknown: string[];
}

export function latexToOmml(tex: string): OmmlResult {
  const clean = tex.replace(/\\(?:label|tag)\{[^}]*\}/g, '').trim();
  try {
    const p = new Parser(tokenize(clean));
    const nodes = p.list(NEVER);
    return { nodes: nodes.length ? nodes : [run(tex)], unknown: [...p.unknown] };
  } catch {
    return { nodes: [run(tex, true)], unknown: [tex] };
  }
}

/** Формула внутри строки текста. */
export const inlineMath = (tex: string): XElement => M('oMath', latexToOmml(tex).nodes);

/** Формула отдельной строкой. */
export const displayMath = (tex: string): XElement => M('oMathPara', [M('oMath', latexToOmml(tex).nodes)]);
